// «Hent arabisk med tashkeel»: typer og rene hjelpere for statusen fra
// /api/v1/admin/quran/tashkeel/*.

import { NorwegianMissingSurah } from "./norwegianSync";

export interface TashkeelReport {
  totalAyahs: number;
  withTashkeel: number;
  missingAyahs: number;
  plainWithFullTashkeel: number;
  surahs: NorwegianMissingSurah[];
  source: string;
  attribution: string;
  generatedAt?: string;
}

export interface TashkeelMismatch {
  surahNumber: number;
  ayahNumber: number;
  similarity?: number | null;
  reason: string;
}

export interface TashkeelOffsetSurah {
  surahNumber: number;
  name: string;
  offset: number;
  note: string;
}

export interface TashkeelFailedSurah {
  surahNumber: number;
  name: string;
  message: string;
}

export type TashkeelSyncState = "idle" | "running" | "done" | "failed";

export interface TashkeelSyncStatus {
  state: TashkeelSyncState;
  alreadyRunning?: boolean;
  overwrite?: boolean;
  startedAt?: string | null;
  finishedAt?: string | null;
  source?: string | null;
  attribution?: string | null;
  sourceSurahs: number;
  sourceAyahs: number;
  dbAyahs: number;
  totalSurahs: number;
  processedSurahs: number;
  currentSurah?: number | null;
  matchedAyahs: number;
  updatedAyahs: number;
  unchangedAyahs: number;
  keptExisting: number;
  mismatches: TashkeelMismatch[];
  offsetSurahs: TashkeelOffsetSurah[];
  unusedSourceAyahs: string[];
  missingInSource: string[];
  withTashkeelAfter?: number | null;
  missingAfter?: number | null;
  failedSurahs: TashkeelFailedSurah[];
  message?: string | null;
}

export const TANZIL_URL = "https://tanzil.net";

export const isTashkeelRunning = (status: TashkeelSyncStatus | null): boolean =>
  status?.state === "running";

/** Fremdrift i prosent (0–100), basert på surah-er som er behandlet. */
export const tashkeelProgressPercent = (status: TashkeelSyncStatus): number => {
  if (status.state === "done") return 100;
  if (!status.totalSurahs) return 0;
  return Math.min(
    100,
    Math.round((status.processedSurahs / status.totalSurahs) * 100),
  );
};

/** Kort oppsummering av en ferdig eller pågående synk. */
export const tashkeelStatusSummary = (status: TashkeelSyncStatus): string => {
  switch (status.state) {
    case "running":
      return status.totalSurahs
        ? `Lagrer tashkeel … ${status.processedSurahs} av ${status.totalSurahs} surah-er` +
            (status.currentSurah ? ` (nå: surah ${status.currentSurah})` : "") +
            `, ${status.updatedAyahs} vers oppdatert så langt.`
        : (status.message ?? "Henter arabisk med tashkeel …");
    case "done": {
      const parts = [
        `Ferdig: ${status.updatedAyahs} vers oppdatert`,
        `${status.unchangedAyahs} var like fra før`,
      ];
      if (status.keptExisting)
        parts.push(`${status.keptExisting} med egen tekst ble beholdt`);
      if (status.mismatches.length)
        parts.push(`${status.mismatches.length} avvik ble ikke lagret`);
      if (status.failedSurahs.length)
        parts.push(`${status.failedSurahs.length} surah-er feilet`);
      const after =
        status.withTashkeelAfter != null
          ? ` ${status.withTashkeelAfter.toLocaleString("nb-NO")} av ${status.dbAyahs.toLocaleString("nb-NO")} vers har nå tashkeel.`
          : "";
      return `${parts.join(", ")}.${after}`;
    }
    case "failed":
      return status.message ?? "Synken stoppet.";
    default:
      return "";
  }
};

/** «Kilden har 6236 vers, databasen 6235 (1:1 Bismillah brukes ikke).» */
export const describeSourceCount = (status: TashkeelSyncStatus): string => {
  if (!status.sourceAyahs) return "";
  const unused = status.unusedSourceAyahs.length
    ? ` Ikke brukt fra kilden: ${status.unusedSourceAyahs.join(", ")}.`
    : "";
  return (
    `Kilden har ${status.sourceAyahs.toLocaleString("nb-NO")} vers, databasen ` +
    `${status.dbAyahs.toLocaleString("nb-NO")}; ${status.matchedAyahs.toLocaleString("nb-NO")} er kontrollert som samme vers.` +
    unused
  );
};
